import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDataLayer from '../../../../shared/hooks/global/UseDataLayer';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import { ProjectData } from '../models/ProjectData';
import { createProject } from '../../../../shared/services/projects/ProjectService';
import { getMonitorsForProject } from '../../../../shared/services/monitor/prompt/PromptService';
import { getKeywordsDailyStatsForProject } from '../../../../shared/services/monitor/keywords/stats/KeywordsStatsService';
import {
  getEnabledModelReadiness,
  SetupProgressSnapshot,
} from '../../../../shared/helpers/projectSetupProgress';
import { trackIntercomEvent } from '../../../../shared/services/intercom/trackEvent';
import { FIRST_CHATBEAT_PROJECT_KEY } from '../../preview/helpers/ProjectPreviewHelper';
import { PROJECTS } from '../../../../shared/routes/path';
import {
  PROJECT_SETUP_READY_MS,
  PROJECT_SETUP_MAX_MS,
  PROJECT_SETUP_POLL_MS,
  BAR_FINISH_MS,
} from '../components/analyze-loader/AnalyzeLoader';

// A monitor prompt leaves these the moment its FIRST monitor_data lands → our "first data" signal.
const COLLECTING_STATUSES = new Set(['PENDING', 'REFRESHING']);

const abortableSleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const id = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        resolve();
      },
      { once: true }
    );
  });

// Poll the new project's monitors until at least one has real data — that's when the querying has
// actually produced something and we can drop the loader. Held on-screen for at least MIN (no flash),
// bounded by MAX so it can never hang (then we navigate anyway and the dashboard shows its own
// collecting state). Fetch errors are swallowed and retried; resolves early if `signal` aborts (the
// user hit "back to projects").
const waitForFirstData = async (
  projectId: number,
  features: string[],
  startedAt: number,
  signal: AbortSignal,
  onProgress: (snapshot: SetupProgressSnapshot) => void
) => {
  while (!signal.aborted) {
    const elapsed = Date.now() - startedAt;
    if (elapsed >= PROJECT_SETUP_MAX_MS) return;

    let ready = false;
    try {
      // Prompt statuses drive the overall "X/Y prompts ready" progress; the per-model readiness comes
      // from the same daily-stats source the dashboard uses, so the loader and dashboard never disagree.
      const [prompts, stats] = await Promise.all([
        getMonitorsForProject(projectId),
        getKeywordsDailyStatsForProject(projectId, 'ALL', 'DAILY'),
      ]);
      const promptsTotal = prompts?.length ?? 0;
      const promptsReady = prompts?.filter((p) => !COLLECTING_STATUSES.has(p.status)).length ?? 0;
      const models = getEnabledModelReadiness(features, stats);
      // Ready = every enabled model has produced data (fall back to prompt data if the account has no
      // model-gated features). We hold the loader until then so the redirect fires only once everything
      // the dashboard will show has actually collected — never while "collecting" is still in progress.
      ready = models.length > 0 ? models.every((m) => m.hasData) : promptsReady > 0;
      onProgress({ models, promptsTotal, promptsReady });
    } catch {
      // transient — keep polling until data or the cap
    }
    // Follow the loader's step timeline: once data is ready we finish after the 44s mark (steps 1-3 +
    // a short tail); otherwise we hold until the hard cap. This keeps the redirect in sync with the
    // paced steps instead of snapping away the moment data lands.
    if (ready && elapsed >= PROJECT_SETUP_READY_MS) return;

    await abortableSleep(PROJECT_SETUP_POLL_MS, signal);
  }
};

export const useProjectSubmit = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { accountResponse, refetchAccountSilently, refetchProjectsSilently } = useAccount();
  const { pushDataLayer } = useDataLayer();

  // Set once the project actually exists → the caller reveals the "back to projects" escape on the loader.
  const [createdProjectId, setCreatedProjectId] = useState<number | null>(null);
  // Drives the bar to 100% — flipped only when the real work is done (first data / cap), not on a timer.
  const [loaderCompleted, setLoaderCompleted] = useState(false);
  // Live per-model + prompt readiness shown on the setup loader; refreshed on each poll.
  const [setupProgress, setSetupProgress] = useState<SetupProgressSnapshot | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const submitProject = async (projectData: ProjectData) => {
    const startedAt = Date.now();
    setCreatedProjectId(null);
    setLoaderCompleted(false);
    setSetupProgress(null);
    // Local const so narrowing survives the awaits below (a ref's `.current` doesn't); the ref is only
    // for the escape handler to reach this run's controller.
    const controller = new AbortController();
    abortRef.current = controller;

    const projectsResponseData = await createProject(projectData);
    if (!projectsResponseData?.projectsResponse?.length) {
      throw new Error(t('projectCreationFailed'));
    }

    const newProjectId = projectsResponseData.newProjectId;

    if (accountResponse) {
      pushDataLayer({
        event: 'add_project',
        project_id: newProjectId,
        email_hashed: accountResponse.email,
        user_id: accountResponse.userId.toString(),
        account_id: accountResponse.accountId.toString(),
      });
    }

    if (accountResponse?.allMonitorsCount === 0) {
      // Onboarding checklist (Intercom): deliberately only for the account's
      // FIRST project — same pre-creation signal as FIRST_CHATBEAT_PROJECT_KEY.
      void trackIntercomEvent(accountResponse, 'onboarding_project_created', {
        projectId: newProjectId,
      });
      localStorage.setItem(FIRST_CHATBEAT_PROJECT_KEY, 'pending_first_view');
    }

    // Refresh account + project list silently (no full-page Wrapper loader) before navigating.
    await Promise.all([refetchAccountSilently(), refetchProjectsSilently()]);

    // The project now exists and is querying in the background — reveal the escape hatch, then hold the
    // loader until its first real data lands (or the cap).
    setCreatedProjectId(newProjectId);
    await waitForFirstData(
      newProjectId,
      accountResponse?.features ?? [],
      startedAt,
      controller.signal,
      setSetupProgress
    );
    if (controller.signal.aborted) return;

    // First data is in (or the cap hit) — complete the bar, hold briefly so it visibly fills, navigate.
    setLoaderCompleted(true);
    await abortableSleep(BAR_FINISH_MS, controller.signal);
    if (!controller.signal.aborted) {
      navigate(`${PROJECTS}/${newProjectId}`);
    }
  };

  // Leave the loader for another route (the project keeps populating server-side). Aborts the poll so
  // its pending `navigate(dashboard)` can't override where the user chose to go.
  const leaveSetup = (path: string) => {
    abortRef.current?.abort();
    navigate(path);
  };

  return {
    submitProject,
    createdProjectId,
    loaderCompleted,
    setupProgress,
    leaveSetup,
  };
};
